<template>
  <div class="aniversariante-lote-modal">
    <modal
      elem-id="aniversariante-lote"
      :show="isVisible"
      :title="t('Enviar Mensagens em Lote')"
      size="medium"
      :close-label="t('Cancelar')"
      :save-label="saveLabel"
      save-icon="pi pi-whatsapp"
      :save-fn="handleSendLote"
      :save-disabled-fn="isSaveDisabled"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <div v-if="hasAniversariantes" class="lote-content">
        <div class="summary-section">
          <div class="summary-info">
            <i class="pi pi-users"></i>
            <div>
              <span class="count">{{ aniversariantes.length }}</span>
              <span class="label">{{ t('aniversariantes selecionados') }}</span>
            </div>
          </div>
        </div>

        <div class="list-section">
          <h4>{{ t('Lista de destinatários:') }}</h4>
          <div class="clientes-list">
            <div
              v-for="aniversariante in aniversariantes"
              :key="aniversariante.cliente_id"
              class="cliente-item"
            >
              <div class="cliente-info">
                <span class="nome">{{ aniversariante.cliente_nome }}</span>
                <span class="telefone">{{ formatPhone(aniversariante) }}</span>
              </div>
              <Tag
                :value="getIdadeLabel(aniversariante)"
                icon="pi pi-calendar"
                severity="info"
              />
            </div>
          </div>
        </div>

        <div class="message-section">
          <label>{{ t('Modelo de mensagem:') }}</label>
          <input-rich-text
            v-model="mensagemModelo"
            data-testid="aniversariantes-lote-message"
            class="message-textarea"
          />
          <small class="placeholders-hint">
            {{ t('Placeholders disponíveis:') }} {nome}, {primeiro_nome}, {idade}, {dia}, {mes}, {franquia}
          </small>
        </div>

        <div v-if="showProgress" class="progress-section">
          <progress-bar
            :value="progressPercent"
            :show-value="true"
          />
          <div class="progress-info">
            <span class="sent">{{ sendResults.sent }} {{ t('enviados') }}</span>
            <span class="pending">{{ sendResults.pending }} {{ t('pendentes') }}</span>
            <span class="error">{{ sendResults.error }} {{ t('erros') }}</span>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAniversariantes } from '@/composables/useAniversariantes';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface Aniversariante {
  cliente_id: Id;
  cliente_nome?: string;
  cliente_ddi?: string;
  cliente_ddd?: string;
  cliente_telefone?: string;
  idade?: number | string;
  [key: string]: unknown;
}

interface Franquia {
  nome: string;
  [key: string]: unknown;
}

interface OpenPayload {
  aniversariantes: Aniversariante[];
  franquiaId: Id;
  franquia?: Franquia;
  mensagemConfig?: string;
}

interface SendResults {
  sent: number;
  pending: number;
  error: number;
}

const { t } = useI18n();
const { marcarEnviado } = useAniversariantes();

const isVisible = ref<boolean>(false);
const aniversariantes = ref<Aniversariante[]>([]);
const franquiaId = ref<Id | null>(null);
const franquia = ref<Franquia | null>(null);
const mensagemModelo = ref<string>('');
const isSending = ref<boolean>(false);
const sendResults = ref<SendResults>({
  sent: 0,
  pending: 0,
  error: 0
});

eventBus.on<OpenPayload>('modal.aniversariante-lote.open', (data?: OpenPayload) => {
  if (!data) return;
  log('AniversarianteLoteModal', 'eventBus', 'Opened with data', data);

  aniversariantes.value = data.aniversariantes || [];
  franquiaId.value = data.franquiaId;
  franquia.value = data.franquia || { nome: '' };
  mensagemModelo.value = data.mensagemConfig || t('Olá, {nome}! 🎉 Feliz aniversário!');

  sendResults.value = { sent: 0, pending: aniversariantes.value.length, error: 0 };

  isVisible.value = true;
});

const hasAniversariantes = computed<boolean>(() => aniversariantes.value.length > 0);
const showProgress = computed<boolean>(() => isSending.value || sendResults.value.sent > 0);

const progressPercent = computed<number>(() => {
  const total = aniversariantes.value.length;
  if (total === 0) return 0;
  return Math.round((sendResults.value.sent / total) * 100);
});

const saveLabel = computed<string>(() =>
  isSending.value ? t('Enviando...') : t('Enviar Todos')
);

const isSaveDisabled = (): boolean =>
  isSending.value || sendResults.value.sent === aniversariantes.value.length;

const formatPhone = (cliente: Aniversariante): string => {
  const { cliente_ddi, cliente_ddd, cliente_telefone } = cliente;
  if (!cliente_telefone) return '-';
  return `${cliente_ddi || ''} (${cliente_ddd || ''}) ${cliente_telefone}`;
};

const getIdadeLabel = (cliente: Aniversariante): string => {
  const idade = Number(cliente.idade ?? 0);
  return `${idade} ${idade === 1 ? t('ano') : t('anos')}`;
};

const handleClose = (): void => {
  if (isSending.value) {
    log('AniversarianteLoteModal', 'handleClose', 'Blocked - still sending');
    return;
  }

  isVisible.value = false;
  aniversariantes.value = [];
  mensagemModelo.value = '';
  sendResults.value = { sent: 0, pending: 0, error: 0 };
};

const handleSendLote = async (): Promise<void> => {
  log('AniversarianteLoteModal', 'handleSendLote', 'Iniciando envio em lote');

  if (aniversariantes.value.length === 0) {
    log('AniversarianteLoteModal', 'handleSendLote', 'No aniversariantes to send');
    return;
  }

  isSending.value = true;
  sendResults.value = { sent: 0, pending: aniversariantes.value.length, error: 0 };

  for (const aniversariante of aniversariantes.value) {
    try {
      await marcarEnviado(
        Number(aniversariante.cliente_id),
        Number(franquiaId.value),
        mensagemModelo.value
      );

      sendResults.value.sent++;
      sendResults.value.pending--;

      log('AniversarianteLoteModal', 'handleSendLote', 'Sent to', aniversariante.cliente_nome);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      sendResults.value.error++;
      sendResults.value.pending--;
      log('AniversarianteLoteModal', 'handleSendLote', 'Error', message);
    }
  }

  isSending.value = false;

  eventBus.emit('aniversariantes.refresh');
  log('AniversarianteLoteModal', 'handleSendLote', 'Emitted aniversariantes.refresh event');

  setTimeout(() => {
    handleClose();
  }, 1500);
};
</script>

<style scoped>
.lote-content {
  padding: 10px 0;
}

.summary-section {
  margin-bottom: 20px;
}

.summary-info {
  display: flex;
  align-items: center;
  gap: 15px;
  background: var(--primary-50);
  border: 1px solid var(--primary-200);
  border-radius: 8px;
  padding: 20px;
}

.summary-info i {
  font-size: 32px;
  color: var(--primary-500);
}

.summary-info div {
  display: flex;
  flex-direction: column;
}

.summary-info .count {
  font-size: 28px;
  font-weight: bold;
  color: var(--primary-600);
}

.summary-info .label {
  font-size: 14px;
  color: var(--text-color-secondary);
}

.list-section {
  margin-bottom: 20px;
}

.list-section h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: var(--text-color-secondary);
}

.clientes-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  background: var(--surface-50);
}

.cliente-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid var(--surface-border);
}

.cliente-item:last-child {
  border-bottom: none;
}

.cliente-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cliente-info .nome {
  font-weight: 500;
  font-size: 14px;
}

.cliente-info .telefone {
  font-size: 12px;
  color: var(--text-color-secondary);
}

.message-section {
  margin-bottom: 15px;
}

.message-section label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
}

.message-textarea {
  width: 100%;
  font-family: monospace;
}

.placeholders-hint {
  display: block;
  margin-top: 5px;
  color: var(--text-color-secondary);
  font-size: 11px;
}

.progress-section {
  margin-top: 15px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 13px;
}

.progress-info .sent {
  color: var(--green-600);
  font-weight: 500;
}

.progress-info .pending {
  color: var(--orange-600);
  font-weight: 500;
}

.progress-info .error {
  color: var(--red-600);
  font-weight: 500;
}
</style>
